import Button from '_atoms/Button/Button';
import CloseIcon from '_atoms/Icons/Close';
import i18n from '_i18n';
import { JUNE_2026_SUMMER_CAMPAIGN_END } from '_services/Redux/User/Selector/usePremiumInfo';
import useUser from '_services/Redux/User/Selector/useUser';
import { getCurrencyFromCountryCode } from 'Components/Shopify/Utils';
import { useLanguageAndCurrencyConfig } from 'Hooks/Shopify/useLanguageAndCurrencyConfig';
import useStripeCheckout, {
    CheckoutStripeProps,
} from 'Hooks/useStripeCheckout';
import moment from 'moment';
import { CampaignCurrency } from 'Pages/Guest/Onboarding/Offer/OnboardingOfferPage';
import React, { useEffect, useMemo, useState } from 'react';
import '../OnboardingReboarding.scss';
import getOnboardingOfferHeader from './getOnboardingOfferHeader';

export const SUMMER_2026_DISCOUNT_CODE = 'JUN26A';

const SUMMER_OFFER_DISMISSED_KEY = 'summerOfferJune2026:dismissed';

/* default year price
NOK	1068 kr	499 kr	569 kr	53.3%
USD	$107	$49	    $58	54.2%
EUR	€107	€39	    €68	63.6%
GBP	£89	    £39	    £50	56.2%


*/

export const summerOfferPrices: Record<CampaignCurrency, string> = {
    NOK: '499 kr',
    USD: '$49',
    EUR: '€39',
    GBP: '£39',
};

const SUMMER_OFFER_BODY = {
    no: (price: string): string =>
        `Sommertilbud: Få første året med Premium til kun ${price} 🍦 🌸`,
    en: (price: string): string =>
        `Summer Offer: Get your first year of Premium for only ${price} 🍦 🌸`,
};

const SUMMER_OFFER_CTA = {
    no: 'Oppgrader til Premium',
    en: 'Upgrade to Premium',
};

const MS_PER_HOUR = 60 * 60 * 1000;

const useTimeLeft = (endIso: string): { hours: number; days: number } => {
    const compute = (): { hours: number; days: number } => {
        const ms = Math.max(0, moment(endIso).diff(moment()));
        const hours = Math.floor(ms / MS_PER_HOUR);
        return { hours, days: Math.floor(hours / 24) };
    };
    const [timeLeft, setTimeLeft] = useState(compute);

    useEffect(() => {
        setTimeLeft(compute());
        const id = window.setInterval(() => setTimeLeft(compute()), 60 * 1000);
        return () => window.clearInterval(id);
    }, [endIso]);

    return timeLeft;
};

const SummerOfferJune2026: React.FC = () => {
    const language = i18n.language === 'no' ? 'no' : 'en';
    const { user } = useUser();
    const { countryCode } = useLanguageAndCurrencyConfig(user);
    const currency = getCurrencyFromCountryCode(
        countryCode,
    ) as CampaignCurrency;
    const price = summerOfferPrices[currency] ?? summerOfferPrices.USD;

    const [isLoading, setIsLoading] = useState(false);
    const [isDismissed, setIsDismissed] = useState<boolean>(() => {
        try {
            return (
                window.localStorage.getItem(SUMMER_OFFER_DISMISSED_KEY) ===
                'true'
            );
        } catch {
            return false;
        }
    });

    const timeLeft = useTimeLeft(JUNE_2026_SUMMER_CAMPAIGN_END);
    const header = getOnboardingOfferHeader(language, timeLeft);
    const body = SUMMER_OFFER_BODY[language](price);
    const cta = SUMMER_OFFER_CTA[language];

    const checkoutSuccessUrl = useMemo(() => {
        const successUrl = new URL('/onboarding/offer', window.location.origin);
        successUrl.searchParams.set('checkoutSuccess', 'true');
        return successUrl.toString();
    }, []);

    const checkoutParams = useMemo<CheckoutStripeProps>(
        () => ({
            interval: 'year',
            success_url: checkoutSuccessUrl,
            trial: false,
            discount: SUMMER_2026_DISCOUNT_CODE,
        }),
        [checkoutSuccessUrl],
    );

    const checkoutCacheKey = useMemo(
        () => `summer-offer-june-2026:${SUMMER_2026_DISCOUNT_CODE}`,
        [],
    );

    const { prefetchCheckoutUrl, onCheckoutButtonClickWithPrefetch } =
        useStripeCheckout();

    useEffect(() => {
        if (!user || isDismissed) return;
        prefetchCheckoutUrl(checkoutParams, {
            cacheKey: checkoutCacheKey,
            delayMs: 1000,
        });
    }, [
        checkoutCacheKey,
        checkoutParams,
        isDismissed,
        prefetchCheckoutUrl,
        user,
    ]);

    const handleUpgrade = (): void => {
        void onCheckoutButtonClickWithPrefetch({
            ...checkoutParams,
            cacheKey: checkoutCacheKey,
            isLoading: setIsLoading,
        });
    };

    const handleDismiss = (): void => {
        try {
            window.localStorage.setItem(SUMMER_OFFER_DISMISSED_KEY, 'true');
        } catch {
            // ignore — dismiss still works for this render
        }
        setIsDismissed(true);
    };

    if (!user || isDismissed) return null;

    return (
        <div className="onboarding-offer summer-offer-june-2026">
            <button
                type="button"
                aria-label="Close"
                className="summer-offer-close"
                onClick={handleDismiss}
            >
                <CloseIcon size="small" />
            </button>
            <div className="reboarding-offer-header">{header}</div>
            <div className="reboarding-offer-body">{body}</div>
            <Button
                size="medium"
                isLoading={isLoading}
                classes={language}
                onClick={handleUpgrade}
            >
                {cta}
            </Button>
        </div>
    );
};

export default SummerOfferJune2026;
